import * as React from 'react'

import { Box, SearchField, Tabs, Text, useBreakpoint } from '@factorialco/deprecated-design-system'

import Filters from 'modules/core/components/DataTable/components/Filters'
import useData from 'modules/core/components/DataTable/hooks/useData'
import useLoading from 'modules/core/components/DataTable/hooks/useLoading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import Actions from './Actions'
import EditColumnsModal from './EditColumnsModal'
import ExportModal from './ExportModal'
import { FileName } from './types'

type ExportProp<ExportFileName extends string> =
  | {
      disableExport: true
    }
  | {
      exportFileName: FileName<ExportFileName>
      onExportCallback?: () => void
    }

type CustomizationProps<ExportFileName extends string> =
  | {
      disableDefaultActions: true
    }
  | ({
      inlineDefaultActions?: boolean
      disableDefaultActions?: false
      disableEditColumns?: boolean
      customPopoverOptions?: React.ReactNode
    } & ExportProp<ExportFileName>)

export type CustomView = {
  identifier: string
  title: string
}

type ViewProps<CustomViews extends CustomView[]> =
  | {
      customViews: CustomViews
      currentCustomView: CustomViews[number]['identifier']
      setCurrentCustomView: (view: CustomViews[number]['identifier']) => void
    }
  | {
      views?: never
    }

export type HeadBarProps<ExportFileName extends string, CustomViews extends CustomView[]> = {
  title?: string
  search?: {
    value: string // Original component's value is search... Naming could be improved there
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    placeholder: string
    onClick?: () => void
    onFocus?: () => void
    onBlur?: () => void
  } | null
  toolbar?: React.ReactNode
  actions?: React.ReactNode
} & CustomizationProps<ExportFileName> &
  ViewProps<CustomViews>

function HeadBar<T, ExportFileName extends string, CustomViews extends CustomView[]>({
  search,
  toolbar,
  actions,
  title,
  ...props
}: HeadBarProps<ExportFileName, CustomViews>) {
  const { employee } = useCurrentSession()

  const disableExport = 'disableExport' in props && props.disableExport
  // FIXME: ATM we only support export for employees, as are the ones with a private folder
  const exportIsAvailable = employee?.affiliation !== 'external'
  const disableDefaultActions = 'disableDefaultActions' in props && props.disableDefaultActions
  const disableEditColumns = 'disableEditColumns' in props && props.disableEditColumns
  const exportFileName = 'exportFileName' in props && props.exportFileName
  const customPopoverOptions = 'customPopoverOptions' in props && props.customPopoverOptions
  const onExportCallback = 'onExportCallback' in props && props.onExportCallback
  const inlineDefaultActions = 'inlineDefaultActions' in props && props.inlineDefaultActions

  const { loading: customLoading, queryResult, data, source } = useData<T, ExportFileName>()
  const { loading: queryLoading } = queryResult
  const loading = useLoading(customLoading, queryLoading, data.length, queryResult.data)
  const [exportModalOpen, setExportModalOpen] = React.useState(false)
  const [editColumnsModalOpen, setEditColumnsModalOpen] = React.useState(false)

  const { isMobile } = useBreakpoint()
  const tabs =
    'customViews' in props && 'currentCustomView' in props && 'setCurrentCustomView' in props ? (
      <Tabs currentId={props.currentCustomView} onTabClicked={props.setCurrentCustomView} noBorder>
        {props.customViews.map((view) => (
          <Tabs.Tab id={view.identifier} key={view.identifier} label={view.title} />
        ))}
      </Tabs>
    ) : null

  const actionsComponents = (
    <Box flexDirection="row" gap="s8" flexNoShrink alignItems="center">
      {search && (
        <Box maxWidth="s200">
          <SearchField
            collapsable
            search={search.value}
            onSearch={search.onChange}
            onClick={search.onClick}
            onFocus={search.onFocus}
            onBlur={search.onBlur}
            placeholder={search.placeholder}
          />
        </Box>
      )}
      {actions}
      <Actions
        inlineDefaultActions={!!inlineDefaultActions}
        disableDefaultActions={!!disableDefaultActions}
        disableExport={disableExport}
        exportIsAvailable={exportIsAvailable}
        disableEditColumns={disableEditColumns}
        customPopoverOptions={customPopoverOptions}
        onExportClick={() => {
          setExportModalOpen(true)
        }}
        onEditColumnsClick={() => {
          setEditColumnsModalOpen(true)
        }}
      />
    </Box>
  )

  return (
    <Box flexDirection="column" gap="s16">
      {title && (
        <Text size="400" weight="semibold" htmlTag="h2" color="grey1000">
          {title}
        </Text>
      )}
      {tabs && (
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          border={{
            bottom: {
              style: 'solid',
              width: 's1',
              color: 'grey400',
            },
          }}
          gap="s24"
        >
          <Box flexGrow overflowX="auto">
            {tabs}
          </Box>
          {actionsComponents}
        </Box>
      )}
      <Box
        flexDirection={tabs || isMobile ? 'row' : 'rowReverse'}
        justifyContent="spaceBetween"
        alignItems={tabs ? 'flexStart' : 'flexEnd'}
        gap="s24"
        flexWrap={isMobile ? 'wrap' : 'noWrap'}
      >
        {!tabs && actionsComponents}
        {toolbar}
        <Filters />
      </Box>
      {exportModalOpen && exportFileName && !disableExport && (
        <ExportModal
          source={source}
          fileName={exportFileName}
          loading={loading}
          onClose={() => setExportModalOpen(false)}
          onExportCallback={() => onExportCallback && onExportCallback()}
        />
      )}
      <EditColumnsModal
        onClose={() => setEditColumnsModalOpen(false)}
        open={editColumnsModalOpen}
      />
    </Box>
  )
}

export default HeadBar
